import React, { useMemo, useCallback, useRef, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGlobalProject } from '@/contexts/GlobalProjectContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { EmptyState } from '@/components/analytics/EmptyState';
import LoadingState from '@/components/ui/LoadingState';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ChartDefinitionDrawer,
  toActivitiesUrl,
  toAnalyticsUrl,
  ChartControls,
  ChartMethod,
  getDynamicBarHeight,
  exportChart,
} from '@/components/visuals';
import type { UnitsSpreadBasisValue } from '@/components/visuals';
import type { TopItem } from '@/components/visuals';
import { FocusChartWithTable } from './overview/FocusChartWithTable';
import type { ScheduleAnalyticsShapes } from '@/types/analyticsApi';
import { useChartControls, getControlValue } from '@/hooks/useChartControls';
import { useAnalyticsContext } from '@/hooks/useAnalyticsContext';
import { fetchScheduleAnalytics } from '@/api/analyticsService';
import { ChartCockpit, type CockpitData } from '@/components/analytics/ChartCockpit';
import { useAnalyticsPins } from '@/hooks/useAnalyticsPins';
import {
  CHART_RENDERERS,
  SNAPSHOT_REQUIRED_MESSAGE,
} from './overview/chartRenderers';
import { CHART_REGISTRY, getChartById, type ChartDefinition } from './overview/registry';
import type { ViewMode } from './overview/OverviewToolbar';
import { OverviewFocus } from './overview/OverviewFocus';
import { useAnalyticsChartData as useAnalyticsChartDataHook } from './overview';
import { BUCKET_TO_FLOAT_RANGE } from './overview/helpers';
import { FloatDistributionChart, CriticalityChart, MilestoneVarianceChart, WBSHotspotsChart, QualityChart } from './overview/charts';
import { getFocusItems, emptyAndTable } from './overview/focusItems';
import type { ChartRenderProps } from './overview/chartRenderers';
import { usePostHog } from '@posthog/react';

const defaultPinnedIds = CHART_REGISTRY.filter((c) => c.pinned).map((c) => c.id);

export default function ChartsPage() {
  const { selectedProject } = useGlobalProject();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const posthog = usePostHog();

  // --- URL param state ---
  const viewParam = (searchParams.get('view') as ViewMode) || 'grid';
  const chartParam = searchParams.get('chart') || '';

  const view: ViewMode = chartParam && viewParam === 'focus' ? 'focus' : 'grid';
  const focusChart = view === 'focus' ? getChartById(chartParam) : undefined;

  const { values: controlValues, handleChange: handleControlChange } = useChartControls(
    focusChart?.controls ?? [],
    focusChart?.id
  );

  const showDataLabels =
    view === 'focus' ? getControlValue(controlValues, 'show-labels', true) : true;
  const {
    loading,
    merged,
    scheduleAnalytics,
    activities,
    trendsData,
    floatDist,
    criticalActivities,
    criticalCount,
    nearCriticalCount,
    monitorCount,
    allMilestones,
    milestonesWithVariance,
    hasBaselineData,
    wbsHotspots,
    qualityChecks,
    renderChart,
  } = useAnalyticsChartDataHook({ showDataLabels });

  const {
    pinnedIds,
    handleTogglePin,
  } = useAnalyticsPins({ defaultPinnedIds, initialShowPinnedOnly: true });
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const [definitionChart, setDefinitionChart] = useState<ChartDefinition | null>(null);
  const { baselineId } = useAnalyticsContext();
  const showBaselineControl = view === 'focus' && focusChart && ['s_curve_activities', 's_curve_duration', 's_curve_wbs', 's_curve_units'].includes(focusChart.id) && getControlValue(controlValues, 'show-baseline', false);
  const [baselineSchedule, setBaselineSchedule] = useState<ScheduleAnalyticsShapes | null>(null);
  useEffect(() => {
    if (!selectedProject?.id || !baselineId || !showBaselineControl) {
      setBaselineSchedule(null);
      return;
    }
    let cancelled = false;
    fetchScheduleAnalytics(selectedProject.id, baselineId).then((data) => {
      if (!cancelled && data) setBaselineSchedule(data as ScheduleAnalyticsShapes);
    });
    return () => { cancelled = true; };
  }, [selectedProject?.id, baselineId, showBaselineControl]);

  // --- URL param updaters ---
  const setUrlParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => {
        if (v === null || v === '') next.delete(k);
        else next.set(k, v);
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const handleSelectChart = useCallback((chartId: string) => {
    setUrlParams({ view: 'focus', chart: chartId });
  }, [setUrlParams]);

  // PostHog: track which chart is viewed in focus mode (duration comes from $pageview time-on-page for this URL)
  useEffect(() => {
    if (!posthog || !selectedProject?.id) return;
    if (view === 'focus' && focusChart) {
      posthog.capture('chart_viewed', {
        chart_id: focusChart.id,
        chart_title: focusChart.title,
        project_id: selectedProject.id,
        view_mode: 'focus',
      });
    } else if (view === 'grid') {
      posthog.capture('chart_overview_viewed', {
        project_id: selectedProject.id,
        view_mode: 'overview',
      });
    }
  }, [view, focusChart, selectedProject?.id, posthog]);

  const handleBackToGrid = useCallback(() => {
    navigate('/app/analytics/catalog');
  }, [navigate]);

  const safeFilename = (s: string) => s.replace(/[^a-zA-Z0-9-_]/g, '_').slice(0, 60);

  const handleExportPNG = useCallback(() => {
    exportChart(chartContainerRef, 'png', `${focusChart ? safeFilename(focusChart.title) : 'chart'}.png`);
  }, [focusChart]);

  const handleExportJPG = useCallback(() => {
    exportChart(chartContainerRef, 'jpeg', `${focusChart ? safeFilename(focusChart.title) : 'chart'}.jpg`);
  }, [focusChart]);

  // --- Cockpit data (KPI strip + change feed derive from merged analytics) ---
  const cockpitData: CockpitData = useMemo(() => ({
    floatDist,
    criticalCount,
    nearCriticalCount,
    monitorCount,
    totalActivities: criticalActivities.length,
    qualityChecks,
    milestonesWithVariance,
    hasBaselineData,
    wbsHotspots,
    hasData: merged !== null,
  }), [floatDist, criticalCount, nearCriticalCount, monitorCount, criticalActivities.length, qualityChecks, milestonesWithVariance, hasBaselineData, wbsHotspots, merged]);

  const handleInfoClick = useCallback((chart: ChartDefinition) => {
    setDefinitionChart(chart);
  }, []);

  // --- Focus-mode chart renderer (applies controls; uses show-labels from controlValues for every chart) ---
  const renderFocusChart = useCallback((chartId: string, height: number): React.ReactNode => {
    const scheduleTyped = (scheduleAnalytics ?? null) as ScheduleAnalyticsShapes | null;
    const puEngine = Boolean(scheduleTyped?.planned_units_time_phased?.engineEnabled);
    const topN = getControlValue(controlValues, 'show-count', 'all');
    const sortOrder = getControlValue(controlValues, 'sort', 'worst-first');
    const includeNegatives = getControlValue(controlValues, 'include-negatives', true);
    const searchQuery = getControlValue(controlValues, 'search', '');
    const showLabels = getControlValue(controlValues, 'show-labels', true);
    const showBaseline = getControlValue(controlValues, 'show-baseline', false);
    const scheduleDateBasisForFocus = getControlValue(controlValues, 'schedule-date-basis', 'baseline');
    const includeMilestonesForFocus = getControlValue(controlValues, 'include-milestones', false);
    const unitsSpreadRaw = getControlValue(controlValues, 'units-spread-basis', 'working');
    const unitsSpreadEffective: UnitsSpreadBasisValue =
      chartId === 'units_per_day_histogram' || chartId === 'planned_units_s_curve'
        ? puEngine
          ? unitsSpreadRaw
          : 'calendar'
        : 'calendar';
    const trendChartIds = ['critical_path_length', 'float_trend', 'near_critical_trend', 'logic_integrity_trend', 'phase_progress_burnup', 'resource_demand_capacity'];
    const maxPoints = trendChartIds.includes(chartId) && typeof topN === 'number' ? topN : undefined;
    const focusOverrides = {
      showDataLabels: showLabels,
      showBaseline: ['s_curve_activities', 's_curve_duration', 's_curve_wbs', 's_curve_units'].includes(chartId) ? showBaseline : undefined,
      baselineScheduleAnalytics: ['s_curve_activities', 's_curve_duration', 's_curve_wbs', 's_curve_units'].includes(chartId) ? baselineSchedule ?? undefined : undefined,
      maxPoints,
      scheduleDateBasis:
        chartId === 'units_per_day_histogram' || chartId === 'planned_units_s_curve'
          ? scheduleDateBasisForFocus
          : undefined,
      includeMilestoneUnits:
        chartId === 'units_per_day_histogram' || chartId === 'planned_units_s_curve'
          ? includeMilestonesForFocus
          : undefined,
      unitsSpreadBasis:
        chartId === 'units_per_day_histogram' || chartId === 'planned_units_s_curve'
          ? unitsSpreadEffective
          : undefined,
    };

    const renderFocus = () => renderChart(chartId, height, 'focus', focusOverrides);

    const focusProps: ChartRenderProps = {
      height,
      density: 'focus',
      showDataLabels: showLabels,
      navigate: (url: string) => navigate(url),
      selectedProjectId: selectedProject?.id,
      floatDist,
      criticalActivities,
      criticalCount,
      nearCriticalCount,
      monitorCount,
      allMilestones,
      milestonesWithVariance,
      hasBaselineData,
      wbsHotspots,
      qualityChecks,
      scheduleAnalytics: scheduleTyped,
      trendsData,
      activities,
      scheduleDateBasis:
        chartId === 'units_per_day_histogram' || chartId === 'planned_units_s_curve'
          ? scheduleDateBasisForFocus
          : 'baseline',
      includeMilestoneUnits:
        chartId === 'units_per_day_histogram' || chartId === 'planned_units_s_curve'
          ? includeMilestonesForFocus
          : false,
      ...(chartId === 'units_per_day_histogram' || chartId === 'planned_units_s_curve'
        ? { unitsSpreadBasis: unitsSpreadEffective }
        : {}),
    };

    if (chartId === 'wbs_hotspots' && wbsHotspots.length > 0) {
      let filtered = [...wbsHotspots];
      if (!includeNegatives) filtered = filtered.filter((w) => w.hotspotScore >= 0);
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        filtered = filtered.filter((w) =>
          (w.wbs_name || '').toLowerCase().includes(q) ||
          (w.wbs_short_name || '').toLowerCase().includes(q) ||
          (w.wbs_id || '').toLowerCase().includes(q)
        );
      }
      filtered.sort((a, b) =>
        sortOrder === 'worst-first' ? b.hotspotScore - a.hotspotScore : a.hotspotScore - b.hotspotScore
      );
      if (topN !== 'all') filtered = filtered.slice(0, topN);
      const dynamicHeight = getDynamicBarHeight(filtered.length, height);
      const items = getFocusItems(chartId, { ...focusProps, wbsHotspots: filtered });
      return (
        <FocusChartWithTable
          chart={<WBSHotspotsChart data={filtered} height={dynamicHeight} showDataLabels={showLabels} density="focus" onItemClick={(wbsId, wbsCode) => navigate(toActivitiesUrl({ wbs_id: wbsId, wbs_code: wbsCode }))} />}
          items={items}
          valueLabel="Score"
          valueFormat={(v) => String(Math.round(v * 10) / 10)}
        />
      );
    }

    if (chartId === 'milestone_variance' && hasBaselineData) {
      let filtered = [...milestonesWithVariance];
      if (!includeNegatives) filtered = filtered.filter((m) => (m.varianceDays ?? 0) >= 0);
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        filtered = filtered.filter((m) => (m.name || '').toLowerCase().includes(q));
      }
      filtered.sort((a, b) =>
        sortOrder === 'worst-first'
          ? (b.varianceDays ?? 0) - (a.varianceDays ?? 0)
          : (a.varianceDays ?? 0) - (b.varianceDays ?? 0)
      );
      if (topN !== 'all') filtered = filtered.slice(0, topN);
      const dynamicHeight = getDynamicBarHeight(filtered.length, height);
      const items = getFocusItems(chartId, { ...focusProps, milestonesWithVariance: filtered });
      return (
        <FocusChartWithTable
          chart={<MilestoneVarianceChart data={filtered} height={dynamicHeight} showDataLabels={showLabels} density="focus" onBarClick={() => navigate(toAnalyticsUrl('milestones'))} />}
          items={items}
          valueLabel="Variance"
          valueFormat={(v) => `${v > 0 ? '+' : ''}${v}d`}
        />
      );
    }

    if (chartId === 'quality_issues' && qualityChecks.length > 0) {
      const items = getFocusItems(chartId, focusProps);
      return (
        <FocusChartWithTable
          chart={<QualityChart data={qualityChecks} height={height} showDataLabels={showLabels} density="focus" onBarClick={() => navigate(toAnalyticsUrl('quality'))} />}
          items={items}
          valueLabel="Count"
        />
      );
    }

    if (chartId === 'float_distribution' && floatDist.length > 0) {
      const items = getFocusItems(chartId, focusProps);
      return (
        <FocusChartWithTable
          chart={
            <FloatDistributionChart
              data={floatDist}
              height={height}
              showDataLabels={showLabels}
              density="focus"
              onBucketClick={(bucket) => {
                if (bucket === '<0') navigate(toActivitiesUrl({ negativeFloat: true }));
                else if (bucket === '0') navigate(toActivitiesUrl({ critical: true }));
                else {
                  const range = BUCKET_TO_FLOAT_RANGE[bucket];
                  if (range) navigate(toActivitiesUrl({ float_min: range.min, float_max: range.max }));
                }
              }}
            />
          }
          items={items}
          valueLabel="Count"
        />
      );
    }

    if (chartId === 'criticality_breakdown' && (criticalCount > 0 || nearCriticalCount > 0 || monitorCount > 0)) {
      const items = getFocusItems(chartId, focusProps);
      return (
        <FocusChartWithTable
          chart={
            <CriticalityChart
              critical={criticalCount}
              nearCritical={nearCriticalCount}
              monitor={monitorCount}
              height={height}
              density="focus"
              onSegmentClick={(band) => {
                if (band === 'critical') navigate(toActivitiesUrl({ critical: true }));
                else navigate(toAnalyticsUrl('critical-path'));
              }}
            />
          }
          items={items}
          valueLabel="Count"
        />
      );
    }

    if (chartId === 'trends_snapshots') {
      return emptyAndTable(renderFocus(), SNAPSHOT_REQUIRED_MESSAGE);
    }

    if (chartId === 's_curve_activities') {
      const items = getFocusItems(chartId, focusProps);
      if (items.length === 0) return emptyAndTable(renderFocus(), 'No data. Import a schedule.');
      return <FocusChartWithTable chart={renderFocus()} items={items} valueLabel="Count" valueFormat={(v) => String(Math.round(Number(v)))} />;
    }
    if (chartId === 's_curve_duration') {
      const items = getFocusItems(chartId, focusProps);
      if (items.length === 0) return emptyAndTable(renderFocus(), 'No duration data. Import a schedule.');
      return <FocusChartWithTable chart={renderFocus()} items={items} valueLabel="Hours" valueFormat={(v) => String(Number(v).toFixed(1))} />;
    }
    if (chartId === 's_curve_units') {
      const items = getFocusItems(chartId, focusProps);
      if (items.length === 0) return emptyAndTable(renderFocus(), 'No units data. Import activities with target_work_qty.');
      return <FocusChartWithTable chart={renderFocus()} items={items} valueLabel="Units" valueFormat={(v) => String(Number(v).toFixed(1))} />;
    }
    if (chartId === 'units_per_day_histogram') {
      return renderFocus();
    }
    if (chartId === 'planned_units_s_curve') {
      const items = getFocusItems(chartId, focusProps);
      if (items.length === 0) {
        return emptyAndTable(
          renderFocus(),
          'No time-phased planned units. Add target_work_qty and dates, or toggle Dates between Baseline (target) and Current (early).'
        );
      }
      return <FocusChartWithTable chart={renderFocus()} items={items} valueLabel="Units" valueFormat={(v) => String(Number(v).toFixed(1))} />;
    }
    if (chartId === 's_curve_wbs') {
      const items = getFocusItems(chartId, focusProps);
      if (items.length === 0) return emptyAndTable(renderFocus(), 'No WBS progress data. Import a schedule with WBS.');
      return <FocusChartWithTable chart={renderFocus()} items={items} valueLabel="Hours" valueFormat={(v) => String(Number(v).toFixed(1))} />;
    }
    if (chartId === 'milestone_on_time') {
      const items = getFocusItems(chartId, focusProps);
      if (items.length === 0) return emptyAndTable(renderFocus(), 'No milestones with target dates. Add target dates to milestones.');
      return <FocusChartWithTable chart={renderFocus()} items={items} valueLabel="On-time %" valueFormat={(v) => `${Number(v).toFixed(1)}%`} />;
    }
    if (chartId === 'resource_demand_capacity') {
      const items = getFocusItems(chartId, focusProps);
      if (items.length === 0) return emptyAndTable(renderFocus(), 'Resource data not available. Import a schedule with resource assignments.');
      return <FocusChartWithTable chart={renderFocus()} items={items} valueLabel="Hours" valueFormat={(v) => String(Number(v).toFixed(1))} />;
    }
    if (chartId === 'critical_path_length' || chartId === 'float_trend' || chartId === 'near_critical_trend' || chartId === 'logic_integrity_trend' || chartId === 'phase_progress_burnup') {
      const items = getFocusItems(chartId, focusProps);
      if (items.length === 0) return emptyAndTable(renderFocus(), SNAPSHOT_REQUIRED_MESSAGE);
      return <FocusChartWithTable chart={renderFocus()} items={items} valueLabel="Value" valueFormat={(v) => (typeof v === 'number' && Number.isInteger(v) ? String(v) : String(Number(v).toFixed(1)))} />;
    }

    return renderChart(chartId, height, 'focus', focusOverrides);
  }, [renderChart, floatDist, criticalActivities, criticalCount, nearCriticalCount, monitorCount, allMilestones, milestonesWithVariance, hasBaselineData, wbsHotspots, qualityChecks, scheduleAnalytics, trendsData, controlValues, navigate, selectedProject?.id, baselineSchedule, activities]);

  // --- Focus-mode: config-driven controls strip above chart ---
  const focusAboveChart = useMemo(() => {
    if (!focusChart?.controls?.length) return undefined;
    return (
      <ChartControls
        controls={focusChart.controls}
        values={controlValues}
        onChange={handleControlChange}
        className="mb-0"
        meta={{
          plannedUnitsEngineEnabled: Boolean(
            (scheduleAnalytics as ScheduleAnalyticsShapes | null)?.planned_units_time_phased?.engineEnabled
          ),
        }}
      />
    );
  }, [focusChart, controlValues, handleControlChange, scheduleAnalytics]);

  const focusBelowChart = useMemo(() => {
    if (!focusChart?.method) return undefined;
    return <ChartMethod method={focusChart.method} />;
  }, [focusChart]);

  // --- Guards ---
  if (!selectedProject) {
    return (
      <div className="flex flex-col h-full">
        <EmptyState />
      </div>
    );
  }

  if (loading && !merged) {
    return (
      <div className="flex flex-col h-full">
        <LoadingState message="Loading analytics…" size="lg" fullScreen={false} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {view === 'focus' && focusChart ? (
        <>
          <PageHeader
            title={focusChart.title}
            subtitle={focusChart.description}
            actions={
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleBackToGrid}>
                  ← Back to all charts
                </Button>
                <Button variant="outline" size="sm" onClick={() => setDefinitionChart(focusChart)}>
                  Info
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      Export
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleExportPNG}>
                      Download PNG
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleExportJPG}>
                      Download JPG
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            }
          />
          <OverviewFocus
            key={focusChart.id}
            chart={focusChart}
            renderChart={renderFocusChart}
            aboveChart={focusAboveChart}
            belowChart={focusBelowChart}
            chartContainerRef={chartContainerRef}
          />
        </>
      ) : (
        <>
          <PageHeader
            title="Overview"
            subtitle="Schedule and risk signals at a glance. Drill into any chart or browse the full library."
          />
          <ChartCockpit
            data={cockpitData}
            singleSnapshot={!(trendsData?.hasHistory && (trendsData?.points?.length ?? 0) >= 2)}
            pinnedIds={pinnedIds}
            renderChart={renderChart}
            onSelectChart={handleSelectChart}
            onTogglePin={handleTogglePin}
            onInfoClick={handleInfoClick}
          />
        </>
      )}
      <ChartDefinitionDrawer
        open={!!definitionChart}
        onClose={() => setDefinitionChart(null)}
        chart={definitionChart}
      />
    </div>
  );
}
